import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ReportTypeIcon,
  Group,
  PersonPage,
  Report,
  ReportList,
  ReportStatus,
  ReportType,
  ReportTypeList,
  Zone,
  ZoneList,
} from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { cn, useToast } from '@up2d8/ui';
import {
  AdminIcon,
  AdminPage,
  Avatar,
  Badge,
  Btn,
  CardHead,
  CardPad,
  Checkbox,
  Drawer,
  Empty,
  Field,
  FilterSelect,
  IconBtn,
  Input,
  Modal,
  PageHead,
  SearchField,
  Sect,
  Segmented,
  Select,
  Textarea,
  Toolbar,
  useAdminToast,
  type BadgeTone,
} from '../admin-kit';
import { LoadError, Loading, useShortWhen } from '../werkvloer';
import { saveBlob } from '../../shared/download';
import { REPORT_TYPE_ICONS, REPORT_TYPE_ICON_KEYS } from '../../shared/report-type-icons';
import { useSession } from '../../shared/session';
import { formatDateTime } from '../../shared/format';

const STATUSES: ReportStatus[] = ['new', 'in_progress', 'resolved', 'rejected'];

/**
 * Welke stap van welke status mag — dezelfde tabel als `floor-reports/internal/domain/status.ts`
 * in de API. Een knop tonen die de server toch weigert, is een fout die pas na Opslaan komt.
 */
const ALLOWED_NEXT: Record<ReportStatus, ReportStatus[]> = {
  new: ['in_progress', 'resolved', 'rejected'],
  in_progress: ['resolved', 'rejected'],
  resolved: ['in_progress'],
  rejected: ['in_progress'],
};

const STATUS_TONE: Record<ReportStatus, BadgeTone> = {
  new: 'solid',
  in_progress: 'attention',
  resolved: 'ok',
  rejected: 'neutral',
};

/** De filter "Status": `open` (nieuw of in behandeling) is de standaard, `all` toont alles. */
type StatusFilter = 'open' | 'all' | ReportStatus;

/**
 * De meldingenlijst voor wie ze opvolgt (Stap 36; herontworpen in BHR-6, frames W2 en W3).
 *
 * Open eerst en oudste bovenaan: wat het langst ligt, is wat het langst wacht. Dat is de omgekeerde
 * volgorde van bijna elk ander scherm in dit platform, en met opzet — een meldingenlijst is een
 * werkvoorraad en geen tijdlijn. Een klik op een melding opent ze rechts in een paneel.
 *
 * Wie `floor_reports.report.manage` heeft, ziet alles en kan de soorten en zones inrichten. Wie
 * alleen mag opvolgen, ziet de soorten die zijn eigen groepen behandelen; dat filter staat op de
 * server, niet hier.
 */
export function ReportsAdminPage() {
  const { t } = useTranslation();
  const session = useSession();
  const toast = useToast();
  const adminToast = useAdminToast();
  const when = useShortWhen();
  const [status, setStatus] = useState<StatusFilter>('open');
  const [typeId, setTypeId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const canManage = session.has('floor_reports.report.manage');
  // "Open" is twee standen; de server filtert op één, dus dat filter doet de lijst hier.
  const serverStatus = status === 'open' || status === 'all' ? '' : status;

  const reports = useQuery({
    queryKey: ['floor-reports', serverStatus, typeId, canManage],
    queryFn: async (): Promise<Report[]> => {
      const { data, error } = await session.api.GET('/api/v1/floor-reports', {
        params: {
          query: {
            ...(serverStatus ? { status: serverStatus } : {}),
            ...(typeId ? { type: typeId } : {}),
            ...(canManage ? { all: 'true' as const } : {}),
          },
        },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ReportList).items;
    },
  });

  const types = useQuery({
    queryKey: ['me', 'floor-reports', 'types'],
    queryFn: async (): Promise<ReportType[]> => {
      const { data } = await session.api.GET('/api/v1/me/floor-reports/types', {});
      return (data as ReportTypeList | undefined)?.items ?? [];
    },
  });

  const zones = useQuery({
    queryKey: ['me', 'floor-reports', 'zones'],
    queryFn: async (): Promise<Zone[]> => {
      const { data } = await session.api.GET('/api/v1/me/floor-reports/zones', {});
      return (data as ZoneList | undefined)?.items ?? [];
    },
  });

  /** Geen `<a href download>`: het token staat in het geheugen (zie `shared/download.ts`). */
  const download = useMutation({
    mutationFn: async () => {
      const { data, error } = await session.api.GET('/api/v1/floor-reports/export.csv', {
        params: {
          query: {
            ...(serverStatus ? { status: serverStatus } : {}),
            ...(canManage ? { all: 'true' as const } : {}),
          },
        },
        parseAs: 'blob',
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      await saveBlob(data as unknown as Blob, 'meldingen.csv');
    },
    onError: () => toast.show({ tone: 'danger', title: t('errors.generic') }),
  });

  const typeById = new Map((types.data ?? []).map((type) => [type.id, type]));
  const visible = (reports.data ?? []).filter(
    (report) =>
      (status !== 'open' || report.status === 'new' || report.status === 'in_progress') &&
      (zoneId === '' || report.zoneId === zoneId),
  );
  const openReport = (reports.data ?? []).find((report) => report.id === openId) ?? null;

  return (
    <AdminPage>
      <PageHead
        title={t('floorReportsAdmin.title')}
        sub={t('beheer.werkvloer.reports.sub')}
        actions={
          <Btn
            variant="sec"
            icon={<AdminIcon name="download" />}
            disabled={download.isPending}
            onClick={() => download.mutate()}
            testId="reports-export"
          >
            {t('floorReportsAdmin.export')}
          </Btn>
        }
      />

      <div className={cn('a-grid', canManage && 'side')}>
        <section className="a-card" aria-label={t('floorReportsAdmin.title')}>
          <Toolbar
            count={
              status === 'open'
                ? t('beheer.werkvloer.reports.countOpen', { count: visible.length })
                : t('beheer.werkvloer.reports.countShown', { count: visible.length })
            }
          >
            <FilterSelect
              label={t('beheer.werkvloer.reports.filterStatus')}
              value={status}
              neutral="all"
              onChange={(value) => setStatus(value as StatusFilter)}
              options={[
                { value: 'open', label: t('beheer.werkvloer.reports.statusOpen') },
                ...STATUSES.map((value) => ({
                  value,
                  label: t(`floorReports.status.${value}`),
                })),
                { value: 'all', label: t('floorReportsAdmin.allStatuses') },
              ]}
              testId="reports-filter-status"
            />
            <FilterSelect
              label={t('floorReportsAdmin.filterType')}
              value={typeId}
              onChange={setTypeId}
              options={[
                { value: '', label: t('floorReportsAdmin.allTypes') },
                ...(types.data ?? []).map((type) => ({ value: type.id, label: type.name })),
              ]}
              testId="reports-filter-type"
            />
            {(zones.data ?? []).length > 0 ? (
              <FilterSelect
                label={t('beheer.werkvloer.reports.filterZone')}
                value={zoneId}
                onChange={setZoneId}
                options={[
                  { value: '', label: t('beheer.werkvloer.reports.allZones') },
                  ...(zones.data ?? []).map((zone) => ({ value: zone.id, label: zone.name })),
                ]}
                testId="reports-filter-zone"
              />
            ) : null}
          </Toolbar>

          {reports.isPending ? (
            <Loading />
          ) : reports.isError ? (
            <LoadError onRetry={() => void reports.refetch()} error={reports.error} />
          ) : visible.length === 0 ? (
            <Empty
              icon={<AdminIcon name="flag" size={24} />}
              title={t('floorReportsAdmin.emptyTitle')}
              text={t('floorReportsAdmin.emptyHint')}
            />
          ) : (
            visible.map((report) => {
              const type = typeById.get(report.typeId);
              return (
                <button
                  key={report.id}
                  type="button"
                  className={cn('a-item wv-row', report.id === openId && 'sel')}
                  onClick={() => setOpenId(report.id)}
                  data-testid="report-card"
                >
                  <TypeTile icon={type?.icon ?? 'triangle'} severe={type?.severe ?? false} />
                  <div className="a-item-info">
                    <div className="a-item-top">
                      <span className="a-name">{reportTitle(report)}</span>
                      <Badge tone={STATUS_TONE[report.status]}>
                        {t(`floorReports.status.${report.status}`)}
                      </Badge>
                    </div>
                    <div className="a-meta">
                      {when(report.submittedAt)} · <Reporter report={report} />
                      {report.assigneeName ? ` · ${report.assigneeName}` : ''}
                    </div>
                    <div className="wv-text">{report.description}</div>
                  </div>
                  {report.fileIds.length > 0 ? (
                    <span
                      className="a-tile navy"
                      style={{ width: 64, height: 64 }}
                      aria-label={t('floorReportsAdmin.photoCount', {
                        count: report.fileIds.length,
                      })}
                      role="img"
                    >
                      <AdminIcon name="image" size={22} />
                    </span>
                  ) : null}
                  <AdminIcon name="chevronRight" size={18} className="wv-chev" />
                </button>
              );
            })
          )}
        </section>

        {canManage ? <SettingsCard onDone={adminToast.show} /> : null}
      </div>

      {openReport ? (
        <ReportDrawer
          report={openReport}
          handler={typeById.get(openReport.typeId)?.handlerGroupName ?? null}
          onClose={() => setOpenId(null)}
          onSaved={(message) => {
            setOpenId(null);
            adminToast.show(message);
          }}
        />
      ) : null}
      {adminToast.element}
    </AdminPage>
  );
}

function reportTitle(report: Report): string {
  return report.zoneName ? `${report.typeName} · ${report.zoneName}` : report.typeName;
}

/**
 * Wie meldde. Bij een anonieme melding staat hier geen naam, en ook geen "verborgen": er is niets
 * om te verbergen. Dat verschil is het hele punt van ADR-0062.
 */
function Reporter({ report }: { report: Report }) {
  const { t } = useTranslation();
  if (report.anonymous) return <>{t('beheer.werkvloer.reports.anonymous')}</>;
  return <>{report.reporterName ?? t('floorReportsAdmin.unknownReporter')}</>;
}

/** De tegel zoals de medewerker hem op zijn meldscherm ziet: oranje bij een ernstige soort. */
function TypeTile({ icon, severe }: { icon: ReportTypeIcon; severe: boolean }) {
  const Icon = REPORT_TYPE_ICONS[icon];
  return (
    <span className={cn('a-tile', severe ? 'orange' : 'navy')} aria-hidden="true">
      <Icon size={20} strokeWidth={2} />
    </span>
  );
}

/**
 * Eén melding opvolgen (W3): stand, opmerking voor de melder en een aparte interne notitie.
 *
 * Opslaan doet wat de oude knoppen deden, in één keer: een nieuwe stand gaat met de opmerking mee
 * (`PATCH`), zonder nieuwe stand wordt de opmerking een gewone opmerking, en de interne notitie is
 * altijd een eigen opmerking met `internal`. Bij Opgelost of Afgewezen is de opmerking verplicht:
 * de melder hoort te lezen waarom.
 */
function ReportDrawer({
  report: fromList,
  handler,
  onClose,
  onSaved,
}: {
  report: Report;
  handler: string | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const when = useShortWhen();

  // De melding zelf opnieuw ophalen: de lijst kan gefilterd zijn of achterlopen.
  const detail = useQuery({
    queryKey: ['floor-reports', 'detail', fromList.id],
    queryFn: async (): Promise<Report> => {
      const { data, error } = await api.GET('/api/v1/floor-reports/{reportId}', {
        params: { path: { reportId: fromList.id } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as Report;
    },
    placeholderData: fromList,
  });
  const report = detail.data ?? fromList;

  const [status, setStatus] = useState<ReportStatus>(fromList.status);
  const [comment, setComment] = useState('');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);

  const statusChanged = status !== report.status;
  const needsComment = statusChanged && (status === 'resolved' || status === 'rejected');
  const commentMissing = needsComment && comment.trim() === '';

  const save = useMutation({
    mutationFn: async () => {
      if (statusChanged) {
        const { error } = await api.PATCH('/api/v1/floor-reports/{reportId}', {
          params: { path: { reportId: report.id } },
          body: { status, comment },
        });
        if (error) throw error;
      } else if (comment.trim() !== '') {
        const { error } = await api.POST('/api/v1/floor-reports/{reportId}/comments', {
          params: { path: { reportId: report.id } },
          body: { comment, internal: false },
        });
        if (error) throw error;
      }
      if (note.trim() !== '') {
        const { error } = await api.POST('/api/v1/floor-reports/{reportId}/comments', {
          params: { path: { reportId: report.id } },
          body: { comment: note, internal: true },
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['floor-reports'] });
      onSaved(
        statusChanged ? t('floorReportsAdmin.statusChanged') : t('floorReportsAdmin.commentAdded'),
      );
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const nothingToSave = !statusChanged && comment.trim() === '' && note.trim() === '';

  return (
    <Drawer
      open
      onClose={onClose}
      title={reportTitle(report)}
      sub={
        handler
          ? t('floorReportsAdmin.handledBy', { name: handler })
          : t('floorReportsAdmin.noHandler')
      }
      testId="report-drawer"
      footer={
        report.canHandle ? (
          <>
            <Btn variant="sec" onClick={onClose}>
              {t('common.cancel')}
            </Btn>
            <Btn
              disabled={nothingToSave || save.isPending}
              onClick={() => {
                setTried(true);
                if (!commentMissing) save.mutate();
              }}
              testId="report-save"
            >
              {t('common.save')}
            </Btn>
          </>
        ) : (
          <Btn variant="sec" onClick={onClose}>
            {t('common.close')}
          </Btn>
        )
      }
    >
      <div className="a-item-top">
        <Badge tone={STATUS_TONE[report.status]}>{t(`floorReports.status.${report.status}`)}</Badge>
        <span className="a-meta">
          {when(report.submittedAt)} · <Reporter report={report} />
        </span>
      </div>
      <div className="wv-text">{report.description}</div>

      {/* Een foto van de server tonen vraagt een ondertekende link per foto; een tegel met icoon
          zegt dat er foto's zijn (zoals bij VLR, deviations.md). */}
      {report.fileIds.length > 0 ? (
        <div
          className="a-actions"
          role="img"
          aria-label={t('floorReportsAdmin.photoCount', { count: report.fileIds.length })}
        >
          {report.fileIds.map((fileId) => (
            <span key={fileId} className="a-tile navy" style={{ width: 120, height: 90 }}>
              <AdminIcon name="image" size={26} />
            </span>
          ))}
        </div>
      ) : null}

      {report.canHandle ? (
        <>
          <div className="a-field">
            <div className="a-label" id="report-status-label">
              {t('floorReportsAdmin.filterStatus')}
            </div>
            <Segmented
              labelledBy="report-status-label"
              value={status}
              onChange={setStatus}
              // De huidige stand blijft staan (en de gekozen), daarnaast alleen wat de server aanneemt.
              options={STATUSES.filter(
                (value) =>
                  value === report.status ||
                  value === status ||
                  ALLOWED_NEXT[report.status].includes(value),
              ).map((value) => ({
                value,
                label: t(`floorReports.status.${value}`),
                testId: `report-status-${value}`,
              }))}
            />
          </div>

          <Field
            label={t('beheer.werkvloer.reports.commentForReporter')}
            optional={!needsComment}
            hint={
              report.anonymous
                ? t('beheer.werkvloer.reports.anonymousNoFeedback')
                : t('beheer.werkvloer.reports.readsIn', {
                    name: report.reporterName ?? t('floorReportsAdmin.unknownReporter'),
                  })
            }
            error={tried && commentMissing ? t('beheer.werkvloer.reports.commentRequired') : null}
          >
            {(props) => (
              <Textarea
                {...props}
                value={comment}
                maxLength={1000}
                onChange={(event) => setComment(event.target.value)}
                data-testid="report-comment"
              />
            )}
          </Field>

          <Field label={t('beheer.werkvloer.reports.internalNote')} optional>
            {(props) => (
              <Textarea
                {...props}
                value={note}
                maxLength={1000}
                placeholder={t('beheer.werkvloer.reports.internalPlaceholder')}
                onChange={(event) => setNote(event.target.value)}
                data-testid="report-internal-note"
              />
            )}
          </Field>

          <AssigneePicker report={report} />
        </>
      ) : null}

      {/* De geschiedenis: elke stap met wie en wanneer (acceptatiecriterium 2). */}
      <Sect title={t('beheer.werkvloer.reports.history')}>
        <ol className="wv-log" data-testid="report-history">
          {report.events.map((event) => (
            <li key={event.id}>
              <span className="a-muted">
                {formatDateTime(event.at)}
                {event.actorName ? ` · ${event.actorName}` : ''}
              </span>{' '}
              {event.kind === 'status_changed' && event.toStatus
                ? t('floorReportsAdmin.movedTo', {
                    status: t(`floorReports.status.${event.toStatus}`),
                  })
                : event.kind === 'assigned'
                  ? t('floorReportsAdmin.assigned')
                  : event.kind === 'submitted'
                    ? t('floorReportsAdmin.submitted')
                    : ''}
              {event.comment ? ` — ${event.comment}` : ''}
              {event.internal ? (
                <>
                  {' '}
                  <Badge>{t('floorReportsAdmin.internal')}</Badge>
                </>
              ) : null}
            </li>
          ))}
        </ol>
      </Sect>
    </Drawer>
  );
}

/**
 * Een melding aan iemand toewijzen (Extra 6, punt 7).
 *
 * De API kon dit al (`PATCH /reports/:id` met `assigneeId`); er was alleen geen manier om het te
 * doen. Zonder toewijzing is een wachtrij een lijst waar iedereen naar kijkt en niemand iets mee
 * doet.
 *
 * **Zoeken en geen keuzelijst.** Bij een paar duizend medewerkers is een `<select>` onwerkbaar, en
 * wie een melding toewijst, weet aan wie. Dezelfde vorm als het toevoegen van een groepslid.
 *
 * De lijst is niet beperkt tot wie opvolgrechten heeft: `directory` weet niet wie dat zijn, en de
 * server beslist alsnog. Wie geen toegang heeft tot deze meldingen, ziet ze ook na toewijzing
 * niet - de toewijzing is dan zichtbaar maar zonder gevolg, en dat is beter dan een kiezer die
 * stilletjes mensen weglaat die het wél mogen.
 */
function AssigneePicker({ report }: { report: Report }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const candidates = useQuery({
    enabled: search.trim().length >= 2,
    queryKey: ['floor-reports', 'assignees', search],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/people', {
        params: { query: { search: search.trim(), limit: '8', status: 'active' } } as never,
      });
      return (data as PersonPage | undefined)?.items ?? [];
    },
  });

  const assign = useMutation({
    mutationFn: async (assigneeId: string | null) => {
      const { error } = await api.PATCH('/api/v1/floor-reports/{reportId}', {
        params: { path: { reportId: report.id } },
        body: { assigneeId, comment: '' } as never,
      });
      if (error) throw error;
    },
    onSuccess: async (_result, assigneeId) => {
      setSearch('');
      await queryClient.invalidateQueries({ queryKey: ['floor-reports'] });
      toast.show({
        tone: 'success',
        title: assigneeId ? t('floorReportsAdmin.assignedOk') : t('floorReportsAdmin.unassignedOk'),
      });
    },
    onError: () => toast.show({ tone: 'danger', title: t('errors.generic') }),
  });

  return (
    <Sect title={t('floorReportsAdmin.assignTo')}>
      <div className="a-form" data-testid="assignee-picker">
        <div className="a-inline">
          <span className="a-muted">
            {report.assigneeName
              ? t('floorReportsAdmin.assignedTo', { name: report.assigneeName })
              : t('floorReportsAdmin.noAssignee')}
          </span>
          {report.assigneeId ? (
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => assign.mutate(null)}
              testId="assignee-clear"
            >
              {t('floorReportsAdmin.unassign')}
            </Btn>
          ) : null}
        </div>
        <div>
          <SearchField
            label={t('floorReportsAdmin.assignTo')}
            placeholder={t('groups.addMemberHint')}
            value={search}
            onChange={setSearch}
            testId="assignee-search"
          />
          {candidates.data && candidates.data.length > 0 ? (
            <ul className="wv-options">
              {candidates.data.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    data-testid="assignee-option"
                    disabled={assign.isPending}
                    onClick={() => assign.mutate(person.id)}
                  >
                    <Avatar name={`${person.firstName} ${person.lastName}`} size="sm" />
                    <span>
                      {person.firstName} {person.lastName}
                      {person.employeeNumber ? (
                        <span className="a-muted"> · {person.employeeNumber}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </Sect>
  );
}

/**
 * De inrichting, compact rechts: welke soorten meldingen bestaan, wie behandelt ze, en mag het
 * anoniem; daaronder de zones op één regel.
 */
function SettingsCard({ onDone }: { onDone: (message: string) => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ReportType | 'new' | null>(null);
  const [zoneOpen, setZoneOpen] = useState(false);
  const [zoneName, setZoneName] = useState('');

  const types = useQuery({
    queryKey: ['floor-reports', 'settings', 'types'],
    queryFn: async (): Promise<ReportType[]> => {
      const { data, error } = await api.GET('/api/v1/floor-reports/settings/types', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ReportTypeList).items;
    },
  });

  const zones = useQuery({
    queryKey: ['floor-reports', 'settings', 'zones'],
    queryFn: async (): Promise<Zone[]> => {
      const { data } = await api.GET('/api/v1/floor-reports/settings/zones', {});
      return (data as ZoneList | undefined)?.items ?? [];
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['floor-reports'] });
    void queryClient.invalidateQueries({ queryKey: ['me', 'floor-reports'] });
  };

  const toggleType = useMutation({
    mutationFn: async (input: { typeId: string; active: boolean }) => {
      const { error } = await api.PATCH('/api/v1/floor-reports/settings/types/{typeId}', {
        params: { path: { typeId: input.typeId } },
        body: { active: input.active },
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const createZone = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/floor-reports/settings/zones', {
        body: { siteId: null, name: zoneName },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      setZoneName('');
      setZoneOpen(false);
      onDone(t('floorReportsAdmin.zoneCreated'));
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <section className="a-card" data-testid="reports-settings">
      <CardHead title={t('floorReportsAdmin.settings')} />
      <CardPad>
        {types.isPending ? (
          <Loading />
        ) : types.isError ? (
          <LoadError onRetry={() => void types.refetch()} error={types.error} />
        ) : (
          (types.data ?? []).map((type) => (
            <div
              key={type.id}
              className="a-item"
              style={{ padding: 0 }}
              data-testid="report-type-row"
            >
              <div className="a-item-info">
                <div className="a-item-top">
                  <span className="a-name">{type.name}</span>
                  {!type.active ? <Badge>{t('beheer.werkvloer.reports.typeOff')}</Badge> : null}
                  {/* Een actieve soort zonder bestaande behandelgroep (hertest 29 okt.). */}
                  {type.handlerMissing && type.active ? (
                    <Badge tone="danger">{t('floorReportsAdmin.handlerMissing')}</Badge>
                  ) : null}
                  {type.severe ? (
                    <Badge tone="attention">{t('floorReportsAdmin.severe')}</Badge>
                  ) : null}
                  {type.anonymousAllowed ? (
                    <Badge tone="info">{t('beheer.werkvloer.reports.anonymousAllowed')}</Badge>
                  ) : null}
                  {type.openCount > 0 ? (
                    <Badge tone="attention">
                      {t('floorReportsAdmin.openCount', { count: type.openCount })}
                    </Badge>
                  ) : null}
                </div>
                <div className="a-meta">
                  {type.handlerGroupName
                    ? t('floorReportsAdmin.handledBy', { name: type.handlerGroupName })
                    : t('floorReportsAdmin.noHandler')}
                  {type.handlerMissing && type.active
                    ? ` · ${t('floorReportsAdmin.handlerMissingHint')}`
                    : null}
                </div>
              </div>
              <IconBtn
                label={t('common.edit')}
                icon={<AdminIcon name="pencil" />}
                onClick={() => setEditing(type)}
                testId="report-type-edit"
              />
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => toggleType.mutate({ typeId: type.id, active: !type.active })}
                testId="report-type-toggle"
              >
                {type.active ? t('floorReportsAdmin.disable') : t('floorReportsAdmin.enable')}
              </Btn>
            </div>
          ))
        )}
        <div>
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="plus" />}
            onClick={() => setEditing('new')}
            testId="report-type-new"
          >
            {t('floorReportsAdmin.addType')}
          </Btn>
        </div>

        <div className="a-sect-title" style={{ marginTop: 6 }}>
          {t('floorReportsAdmin.zones')}
        </div>
        <div className="a-muted" data-testid="zone-list">
          {(zones.data ?? []).length > 0
            ? (zones.data ?? []).map((zone) => zone.name).join(' · ')
            : t('floorReportsAdmin.zonesHint')}
        </div>
        <div>
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="plus" />}
            onClick={() => setZoneOpen(true)}
            testId="zone-new"
          >
            {t('floorReportsAdmin.addZone')}
          </Btn>
        </div>
      </CardPad>

      {editing ? (
        <TypeDrawer
          type={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            invalidate();
            onDone(message);
          }}
        />
      ) : null}

      <Modal
        open={zoneOpen}
        onClose={() => setZoneOpen(false)}
        title={t('floorReportsAdmin.addZone')}
        sub={t('floorReportsAdmin.zonesHint')}
        footer={
          <>
            <Btn variant="sec" onClick={() => setZoneOpen(false)}>
              {t('common.cancel')}
            </Btn>
            <Btn
              disabled={zoneName.trim() === '' || createZone.isPending}
              onClick={() => createZone.mutate()}
              testId="zone-create"
            >
              {t('floorReportsAdmin.addZone')}
            </Btn>
          </>
        }
      >
        <Field label={t('floorReportsAdmin.zoneName')}>
          {(props) => (
            <Input
              {...props}
              value={zoneName}
              maxLength={80}
              onChange={(event) => setZoneName(event.target.value)}
              data-testid="zone-name"
            />
          )}
        </Field>
      </Modal>
    </section>
  );
}

/**
 * Een soort toevoegen of bewerken, in een paneel: naam, korte uitleg, behandelende groep, icoon,
 * anoniem toelaten en ernstig.
 *
 * Anoniem toelaten is de belangrijkste keuze van deze module, en daarom staat de uitleg ernaast in
 * plaats van in een handleiding: anoniem toelaten betekent dat er geen melder bewaard wordt, en dus
 * dat terugkoppelen niet kan.
 */
function TypeDrawer({
  type,
  onClose,
  onSaved,
}: {
  type: ReportType | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();

  const [name, setName] = useState(type?.name ?? '');
  const [description, setDescription] = useState(type?.description ?? '');
  const [handlerGroupId, setHandlerGroupId] = useState(type?.handlerGroupId ?? '');
  const [anonymousAllowed, setAnonymousAllowed] = useState(type?.anonymousAllowed ?? false);
  const [icon, setIcon] = useState<ReportTypeIcon>(type?.icon ?? 'triangle');
  const [severe, setSevere] = useState(type?.severe ?? false);

  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: async (): Promise<Group[]> => {
      const { data } = await api.GET('/api/v1/groups');
      return ((data as { items: Group[] } | undefined)?.items ?? []) as Group[];
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name,
        description,
        handlerGroupId: handlerGroupId || null,
        anonymousAllowed,
        icon,
        severe,
      };
      if (type) {
        const { error } = await api.PATCH('/api/v1/floor-reports/settings/types/{typeId}', {
          params: { path: { typeId: type.id } },
          body,
        });
        if (error) throw error;
        return;
      }
      const { error } = await api.POST('/api/v1/floor-reports/settings/types', { body });
      if (error) throw error;
    },
    onSuccess: () =>
      onSaved(type ? t('beheer.werkvloer.reports.typeSaved') : t('floorReportsAdmin.typeCreated')),
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const title = type ? t('beheer.werkvloer.reports.editType') : t('floorReportsAdmin.addType');

  return (
    <Drawer
      open
      onClose={onClose}
      title={title}
      sub={t('floorReportsAdmin.settingsIntro')}
      testId="report-type-drawer"
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            disabled={name.trim() === '' || save.isPending}
            onClick={() => save.mutate()}
            testId="report-type-create"
          >
            {type ? t('common.save') : t('floorReportsAdmin.addType')}
          </Btn>
        </>
      }
    >
      <Field label={t('floorReportsAdmin.typeName')}>
        {(props) => (
          <Input
            {...props}
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value)}
            data-testid="report-type-name"
          />
        )}
      </Field>
      <Field label={t('floorReportsAdmin.typeDescription')} optional>
        {(props) => (
          <Input
            {...props}
            value={description}
            maxLength={300}
            onChange={(event) => setDescription(event.target.value)}
            data-testid="report-type-description"
          />
        )}
      </Field>
      <Field
        label={t('floorReportsAdmin.handlerGroup')}
        hint={t('floorReportsAdmin.handlerGroupHint')}
      >
        {(props) => (
          <Select
            {...props}
            value={handlerGroupId}
            onChange={(event) => setHandlerGroupId(event.target.value)}
            data-testid="report-type-group"
          >
            <option value="">{t('floorReportsAdmin.noHandler')}</option>
            {(groups.data ?? []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label={t('floorReportsAdmin.icon')} hint={t('floorReportsAdmin.iconHint')}>
        {(props) => (
          <Select
            {...props}
            value={icon}
            onChange={(event) => setIcon(event.target.value as ReportTypeIcon)}
            data-testid="report-type-icon"
          >
            {REPORT_TYPE_ICON_KEYS.map((key) => (
              <option key={key} value={key}>
                {t(`floorReportsAdmin.icons.${key}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Checkbox
        checked={anonymousAllowed}
        onChange={setAnonymousAllowed}
        label={t('beheer.werkvloer.reports.allowAnonymous')}
        hint={t('floorReportsAdmin.anonymousExplained')}
        testId="report-type-anonymous"
      />
      <Checkbox
        checked={severe}
        onChange={setSevere}
        label={t('floorReportsAdmin.severe')}
        hint={t('floorReportsAdmin.severeExplained')}
        testId="report-type-severe"
      />
    </Drawer>
  );
}
